/**
 * useWebPush — VÉRAULT Web Push subscription hook (Wave 12.t)
 * ────────────────────────────────────────────────────────────
 * Subscribes the current device to the backend's VAPID-signed push
 * stream. Idempotent: re-subscribing returns the existing record.
 *
 * Behaviour:
 *   · Returns immediately on dev hosts (the dev-kill SW prevents push
 *     from working on preview — production only).
 *   · Returns gracefully when the browser doesn't support PushManager
 *     (older Safari, iOS < 16.4, in-app webviews).
 *   · Auto-prompts for notification permission on first call.
 *
 * Usage:
 *   const { canSubscribe, isSubscribed, subscribe, error } = useWebPush();
 *   <button onClick={subscribe}>Enable trip alerts</button>
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "../index";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

function isDevHost() {
  const host = (typeof window !== "undefined" && window.location?.hostname) || "";
  return host === "localhost" || host === "127.0.0.1"
    || /\.preview\.emergentagent\.com$/.test(host)
    || /\.emergent\.host$/.test(host);
}

export function useWebPush() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState(null);
  const [vapidKey, setVapidKey] = useState(null);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && !isDevHost();
    setSupported(ok);
    if (!ok) return;
    api.get("/push/vapid-public-key")
      .then((r) => setVapidKey(r.data?.public_key))
      .catch(() => setVapidKey(null));
    navigator.serviceWorker.ready.then(async (reg) => {
      try {
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(Boolean(sub));
      } catch {
        /* ignore */
      }
    });
  }, []);

  const subscribe = useCallback(async () => {
    setError(null);
    if (!supported || !vapidKey) {
      setError("Push notifications aren't available on this device.");
      return null;
    }
    try {
      if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
        const p = await Notification.requestPermission();
        setPermission(p);
        if (p !== "granted") {
          setError("Notification permission denied.");
          return null;
        }
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        expirationTime: sub.expirationTime,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        user_agent: navigator.userAgent,
        label: "VÉRAULT Member Suite",
      });
      setIsSubscribed(true);
      return sub;
    } catch (e) {
      setError(e?.message || "Couldn't enable notifications.");
      return null;
    }
  }, [supported, vapidKey]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscribe", { params: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (e) {
      setError(e?.message || "Couldn't disable notifications.");
    }
  }, []);

  return {
    supported,
    permission,
    isSubscribed,
    canSubscribe: supported && permission !== "denied" && !isSubscribed,
    subscribe,
    unsubscribe,
    error,
  };
}

export default useWebPush;
